import { useState, useMemo, useCallback, useEffect } from "react";

/**
 * Custom hook managing state, navigation tabs, event filtering,
 * search queries, and personalized recommendations for the Home view.
 */
export function useHomeLogic(
  events = [],
  communities = [],
  matches = [],
  userData = {}
) {
  const [activeTab, setActiveTab] = useState("home");
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Sub-view state: which event/community detail screen (if any) is showing
  // on top of the active tab.
  const [viewingEventDetails, setViewingEventDetails] = useState(null);
  const [selectedProfileCommunity, setSelectedProfileCommunity] =
    useState(null);

  const [selectedCategory, setSelectedCategory] = useState("For You");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isCreatingCommunity, setIsCreatingCommunity] = useState(false);

  // Reset search and category filters when switching users (detected via UID)
  useEffect(() => {
    setSelectedCategory("For You");
    setSearchQuery("");
    setSelectedEvent(null);
    setViewingEventDetails(null);
    setSelectedProfileCommunity(null);
  }, [userData?.uid]);

  /**
   * Changes the active navigation tab and resets specific sub-view states.
   */
  const handleTabChange = useCallback((tabName) => {
    setSelectedEvent(null);
    setViewingEventDetails(null);
    setSelectedProfileCommunity(null);
    setActiveTab(tabName);
  }, []);

  // Normalize props to arrays so the rest of this hook (and the components
  // consuming its output) never has to null-check events/matches/communities
  // — DataContext already does this too, but this hook can be called with
  // plain values directly, so the guard is kept here as well.
  const safeEvents = Array.isArray(events) ? events : [];
  const safeMatches = Array.isArray(matches) ? matches : [];
  const safeCommunities = Array.isArray(communities) ? communities : [];

  /**
   * Filters events dynamically based on category selection and search query.
   */
  const filteredEvents = useMemo(() => {
    return safeEvents.filter((ev) => {
      const matchesCategory =
        selectedCategory === "For You" ||
        ev?.category?.toLowerCase() === selectedCategory.toLowerCase();

      const query = (searchQuery || "").trim().toLowerCase();
      const matchesSearch =
        !query ||
        ev?.title?.toLowerCase().includes(query) ||
        ev?.description?.toLowerCase().includes(query) ||
        ev?.host?.toLowerCase().includes(query) ||
        ev?.venue?.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [safeEvents, selectedCategory, searchQuery]);

  /**
   * Filters communities dynamically based on the search query,
   * supporting multiple naming conventions (name/title, description/desc).
   */
  const filteredCommunities = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return safeCommunities;
    return safeCommunities.filter((c) => {
      const commName = c?.name || c?.title || "";
      const commDesc = c?.description || c?.desc || "";
      return (
        commName.toLowerCase().includes(query) ||
        commDesc.toLowerCase().includes(query)
      );
    });
  }, [safeCommunities, searchQuery]);

  // Featured event banner (defaults to the first filtered or global event)
  const featuredEvent = filteredEvents[0] || safeEvents[0];

  /**
   * Dynamically filters events for the first recommendation row based on user interests.
   * Row 1 uses the user's #1 interest; falls back to all events if they have
   * none set, or if nothing matches that interest.
   */
  const firstRowEvents = useMemo(() => {
    const interests = Array.isArray(userData?.interests)
      ? userData.interests
      : [];
    const primaryInterest = interests[0]
      ? interests[0].toLowerCase().trim()
      : "";

    if (!primaryInterest) return safeEvents;

    const matched = safeEvents.filter(
      (e) =>
        e?.category &&
        (e.category.toLowerCase().trim() === primaryInterest ||
          e.category.toLowerCase().includes(primaryInterest))
    );

    return matched.length > 0 ? matched : safeEvents;
  }, [safeEvents, userData]);

  /**
   * Dynamically filters events for the second recommendation row based on user interests.
   * Same pattern as row 1 but keyed off the #2 interest (falling back to #1
   * if there's no second interest), and offsetting the fallback list by one
   * so it doesn't just repeat row 1 when both rows fall through.
   */
  const secondRowEvents = useMemo(() => {
    const interests = Array.isArray(userData?.interests)
      ? userData.interests
      : [];
    const secondaryInterest = interests[1]
      ? interests[1].toLowerCase().trim()
      : interests[0]
      ? interests[0].toLowerCase().trim()
      : "";

    if (!secondaryInterest) return safeEvents.slice(1);

    const matched = safeEvents.filter(
      (e) =>
        e?.category &&
        (e.category.toLowerCase().trim() === secondaryInterest ||
          e.category.toLowerCase().includes(secondaryInterest))
    );

    return matched.length > 0 ? matched : safeEvents.slice(1);
  }, [safeEvents, userData]);

  /**
   * Generates a third recommendation row based on user interests or fallback slice.
   */
  const thirdRowEvents = useMemo(() => {
    const interests = Array.isArray(userData?.interests)
      ? userData.interests
      : [];
    const tertiaryInterest = interests[2] || interests[1] || "";

    if (!tertiaryInterest) return safeEvents.slice(2);

    const matched = safeEvents.filter(
      (e) =>
        e?.category &&
        e.category.toLowerCase().includes(tertiaryInterest.toLowerCase().trim())
    );

    return matched.length > 0 ? matched : safeEvents.slice(2);
  }, [safeEvents, userData]);

  /**
   * Complete list of all events for the final section.
   */
  const allEvents = useMemo(() => {
    return safeEvents;
  }, [safeEvents]);

  /**
   * Generates a personalized title for the first recommendation row.
   */
  const firstRowTitle = useMemo(() => {
    const interests = Array.isArray(userData?.interests)
      ? userData.interests
      : [];
    const primaryInterest = interests[0] || "Featured Activities";
    return `Because you like ${primaryInterest}`;
  }, [userData]);

  /**
   * Generates a personalized title for the second recommendation row.
   */
  const secondRowTitle = useMemo(() => {
    const interests = Array.isArray(userData?.interests)
      ? userData.interests
      : [];
    const secondaryInterest =
      interests[1] || interests[0] || "Outdoor Adventures";
    return `Recommended for your interest in ${secondaryInterest}`;
  }, [userData]);

  /**
   * Filters events created by the current authenticated user.
   * Matches on creator id/email OR on host name as a fallback, since some
   * events (e.g. seeded ones) only have a host string and no createdBy id.
   */
  const myCreatedEvents = useMemo(() => {
    const currentUserId = userData?.uid || userData?.id;
    const currentUserEmail = userData?.email;
    const currentUserName = userData?.name;

    return safeEvents.filter((e) => {
      const matchesId =
        (currentUserId && e?.createdBy === currentUserId) ||
        (currentUserEmail && e?.createdBy === currentUserEmail);
      const matchesHost =
        currentUserName &&
        e?.host &&
        e.host.toLowerCase() === currentUserName.toLowerCase();
      return matchesId || matchesHost;
    });
  }, [safeEvents, userData]);

  return {
    activeTab,
    setActiveTab,
    selectedEvent,
    setSelectedEvent,
    viewingEventDetails,
    setViewingEventDetails,
    selectedProfileCommunity,
    setSelectedProfileCommunity,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    isCreatingEvent,
    setIsCreatingEvent,
    isCreatingCommunity,
    setIsCreatingCommunity,
    handleTabChange,
    safeEvents,
    safeMatches,
    safeCommunities,
    filteredEvents,
    filteredCommunities,
    featuredEvent,
    firstRowEvents,
    secondRowEvents,
    thirdRowEvents,
    allEvents,
    firstRowTitle,
    secondRowTitle,
    myCreatedEvents,
  };
}

